
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 100  // Special level to disable all logging
}

interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  context?: string;
}

const isProd = (
  (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.PROD) ||
  // Node/TSX runtime fallback
  process.env.NODE_ENV === 'production'
);

const defaultConfig: LoggerConfig = {
  minLevel: isProd ? LogLevel.INFO : LogLevel.DEBUG,
  enableConsole: true
};

function sanitizeLogData(data: any): any {
  if (!data) return data;
  if (typeof data !== 'object') return data;
  
  // Deep clone to avoid modifying the original
  const sanitized = Array.isArray(data) ? [...data] : {...data};
  
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'auth', 'credential', 
    'session', 'cookie', 'jwt', 'apiKey', 'credit_card', 'ssn', 'phone'
  ];
  
  Object.keys(sanitized).forEach(key => {
    const lowerKey = key.toLowerCase();
    
    if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeLogData(sanitized[key]);
    }
  });
  
  return sanitized;
}

export function createLogger(context?: string): Logger {
  return new Logger({
    ...defaultConfig,
    context
  });
}

export class Logger {
  private config: LoggerConfig;
  
  constructor(config: LoggerConfig) {
    this.config = config;
  }
  
  public debug(message: string, ...data: any[]): void {
    this.log(LogLevel.DEBUG, message, ...data);
  }
  
  public info(message: string, ...data: any[]): void {
    this.log(LogLevel.INFO, message, ...data);
  }
  
  public warn(message: string, ...data: any[]): void {
    this.log(LogLevel.WARN, message, ...data);
  }
  
  public error(message: string, ...data: any[]): void {
    this.log(LogLevel.ERROR, message, ...data);
  }
  
  public log(level: LogLevel, message: string, ...data: any[]): void {
    if (!this.config.enableConsole || level < this.config.minLevel) {
      return;
    }
    
    const timestamp = new Date().toISOString();
    const levelName = LogLevel[level];
    const contextPrefix = this.config.context ? `[${this.config.context}] ` : '';
    const formattedMessage = `${timestamp} ${levelName}: ${contextPrefix}${message}`;
    
    const sanitizedData = data.map(item => sanitizeLogData(item));
    
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage, ...sanitizedData);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage, ...sanitizedData);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage, ...sanitizedData);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage, ...sanitizedData);
        break;
    }
  }
  
  public createChild(childContext: string): Logger {
    const parentContext = this.config.context;
    const fullContext = parentContext ? `${parentContext}:${childContext}` : childContext;
    
    return new Logger({
      ...this.config,
      context: fullContext
    });
  }
}

export const logger = createLogger('Rendezvous');

export default logger; 